import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { ActivityIndicator, KeyboardAvoidingView, Platform, ScrollView } from 'react-native';
import { Label, Paragraph, XStack, YStack } from 'tamagui';

import { Text } from '@/components/text';
import { AgeRangePicker } from '@/components/age-range-picker';
import { SolidSurface } from '@/components/glass-surface';
import { ProfileNudgeCard } from '@/components/profile-nudge-card';
import { Heading } from '@/components/heading';
import { FirstRunWizard } from '@/screens/profile/first-run-wizard';
import { Input } from '@/components/input';
import { LocationRadiusPicker } from '@/components/location-radius-picker';
import { PrimaryButton } from '@/components/primary-button';
import { PageEnd } from '@/components/page-end';
import { ScreenContainer } from '@/components/screen-container';
import { useDockClearance, useDockScroll } from '@/lib/dock';
import { SettingsRow, SettingsSection } from '@/components/settings-row';
import { TextLink } from '@/components/text-link';
import { Toggle } from '@/components/toggle';
import { useCurrentLocation } from '@/hooks/use-current-location';
import { useMyProfile } from '@/hooks/use-my-profile';
import { useProfileSave } from '@/hooks/use-profile-save';
import { api, ApiError } from '@/lib/api';
import { HANDLE_POLICY_COPY, MAX_PROMPTS, profileHandleValid } from '@/lib/content-limits';
import { errorMessage } from '@/lib/photo-errors';
import { queryKeys } from '@/lib/query-keys';
import { clampRadius } from '@/lib/radius-bounds';
import { space } from '@/theme/tokens.generated';

// Sensible first-time default -- asking a brand-new user to pick a raw
// meter value with no context is poor UX (see lib/distance.ts's
// radiusHelperText for the human-readable "≈3 miles" translation shown
// next to it -- also reused by the Threads screen's own radius-aware
// eyebrow, issue #151 AC1).
const DEFAULT_RADIUS_M = 5000;

// Real screen, not a stub -- issue #14's create slice, extended by
// issue #136 into a real create-OR-edit form. Originally documented as
// create-only because no GET /me/profile endpoint existed yet -- that
// endpoint has since shipped (identity-and-proximity plan's Task 1),
// but this screen was never updated to actually call it, which meant
// every save silently reset bio/prompts/interests/photos to empty
// (issue #136's review: a real, live data-loss bug once photo uploads
// existed to lose -- tracked separately as #174 for the fuller
// bio/prompts/interests editor this screen still doesn't expose,
// coordinate with issue #4's Rich Profile epic for that). This pass
// fixes the round-trip for every field this screen's own PATCH calls
// touch, without adding UI for fields it doesn't yet let the user edit.
export function Profile() {
  const router = useRouter();
  const dockClearance = useDockClearance();
  const dockScroll = useDockScroll();
  // Kept here (not folded into useProfileSave) because these two
  // invalidations are THIS screen's save-time behavior -- the sibling
  // photo/prompt screens never invalidate them, and per the brief the
  // hook must not change what each screen invalidates today.
  const queryClient = useQueryClient();

  const [handle, setHandle] = useState('');
  // Issue #57: a plain number, not the raw-text-then-parse dance the
  // old numeric Input needed -- LocationRadiusPicker's drag/step
  // controls always produce a valid, bounded, whole-number value by
  // construction (clampRadius), so there is no "user typed something
  // unparseable" state left to represent.
  const [radiusM, setRadiusM] = useState(DEFAULT_RADIUS_M);
  // Issue #315: pre-reveal demographics -- gender/ageRange are plain
  // optional fields (never required, mirrors bio's own "optional" copy
  // register); hideDemographics is the reciprocal opt-out (see the
  // Toggle row below for why it only renders once there's something to
  // hide).
  const [gender, setGender] = useState('');
  const [ageRange, setAgeRange] = useState('');
  const [hideDemographics, setHideDemographics] = useState(false);
  // Issue #174 AC2 -- transient "Saved" confirmation for an edit to an
  // already-existing profile (see the update mutation's own onSuccess
  // comment for why this only fires on edit, not first-time create).
  const [justSaved, setJustSaved] = useState(false);

  const location = useCurrentLocation();

  // retry: false mirrors threads/index.tsx's own ['me','profile']
  // query -- a 404 here means "no Profile yet", a stable outcome (the
  // create path), not a transient failure worth retrying.
  const ownProfile = useMyProfile();

  // Seeds the form from the account's real current values exactly
  // once, the first time they load -- a ref (not a dependency-driven
  // effect keyed on ownProfile.data) so a later automatic refetch
  // (e.g. this same query invalidated elsewhere in the app) never
  // clobbers in-progress edits the user hasn't saved yet.
  const hasPrefilled = useRef(false);

  useEffect(() => {
    if (hasPrefilled.current || !ownProfile.isSuccess) return;

    hasPrefilled.current = true;
    setHandle(ownProfile.data.handle);
    // clampRadius: the backend only ever validated RadiusM > 0 (no
    // upper bound, see internal/domain/profile.go), so an existing
    // profile could in principle carry a value outside this widget's
    // MIN/MAX_RADIUS_M range -- clamped on load so the picker always
    // shows something real rather than pinning silently at an edge.
    setRadiusM(clampRadius(ownProfile.data.radiusM));
    setGender(ownProfile.data.gender);
    setAgeRange(ownProfile.data.ageRange);
    setHideDemographics(ownProfile.data.hideDemographics);
  }, [ownProfile.isSuccess, ownProfile.data]);

  // Issue #211: a blank required handle field is a bad first moment for
  // an anonymous-first app. Pre-fills the create path (confirmed no
  // Profile yet -- a 404, not just "still loading") with a generated
  // candidate the instant that's known, same ref-guard pattern as the
  // prefill effect above so a later refetch can't clobber a handle the
  // user has since edited or regenerated. Never overwrites the EDIT
  // path (an existing handle stays exactly what the account already
  // has, seeded by the effect above instead).
  const isCreatePath = ownProfile.isError && ownProfile.error instanceof ApiError && ownProfile.error.status === 404;
  const hasAutoGenerated = useRef(false);
  // Set the moment the user types into the handle field by hand (issue
  // #211 review): a slow network can still have the initial auto-
  // generate request in flight after that, and its response landing
  // afterward must not silently overwrite what they've already typed.
  // Does NOT gate the explicit "Regenerate" tap below -- that's a
  // deliberate request for a new value, not this guard's concern.
  const handleEditedByUser = useRef(false);

  const generateHandle = useMutation({
    mutationFn: api.generateHandle,
    onSuccess: (result) => {
      if (handleEditedByUser.current) return;
      setHandle(result.handle);
    },
  });

  useEffect(() => {
    if (hasAutoGenerated.current || !isCreatePath) return;

    hasAutoGenerated.current = true;
    generateHandle.mutate();
    // generateHandle is a fresh useMutation object every render (same
    // reasoning as this file's other effects never listing their own
    // mutation objects) -- isCreatePath is the only real dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreatePath]);

  // Mirrors the backend's charset/length policy (issue #122) so the
  // form explains a rejection before the round-trip blames the
  // connection.
  const handleValid = profileHandleValid(handle.trim());
  const canSubmit = handleValid && location.phase.phase === 'granted';

  // The full-replace PATCH assembly (and the data-loss history that
  // justified it) moved to lib/profile-draft.ts + hooks/use-profile-save
  // -- this screen sends only the fields it owns: handle, the location
  // pair, radius, and the #315 demographics. bio/prompts/interests/
  // photos ride through from the account's current server state inside
  // buildProfilePayload, which is also what stops a prompts-save from
  // corrupting photo paths or a photo-save from wiping demographics
  // (both live bugs found while extracting this module).
  const update = useProfileSave({
    onSuccess: () => {
      // Task 4's thread list screen keys its query on ['threads'] --
      // invalidating here means a subsequent browse reflects the
      // profile that unblocks it immediately, without a stale 400
      // ("create a profile before browsing threads") surviving the
      // navigation below.
      //
      // ['me'] (prefix match, same broad form use-tier0-state.ts's own
      // Tier 0 pass already uses) matters just as much: (app) is a Tabs
      // navigator, so the threads screen never unmounts while the user
      // is on this Profile tab -- its ['me','profile'] query (identity-
      // and-proximity plan's Task 5, distance display) would otherwise
      // stay cached at its pre-creation 404 (or a stale location after
      // an edit) for the rest of the session, since nothing else ever
      // refetches it. Without this, distance silently never renders on
      // the very first browse after creating a profile -- caught in
      // final review, PR #52.
      queryClient.invalidateQueries({ queryKey: queryKeys.threads() });
      queryClient.invalidateQueries({ queryKey: queryKeys.me() });

      // Issue #174 AC2: a returning user editing their already-saved
      // profile got no confirmation at all -- this mutation's onSuccess
      // unconditionally navigated to Threads, identical to what a
      // brand-new user sees after their FIRST save. For a first save,
      // that's the right onboarding move (go start browsing). For an
      // edit, silently leaving the screen with no feedback reads as
      // "did that even work?" -- ownProfile.isSuccess distinguishes the
      // two: it's already true before this mutation ever fires for an
      // edit (the GET that prefilled the form), false for a first-time
      // create.
      if (ownProfile.isSuccess) {
        setJustSaved(true);
        setTimeout(() => setJustSaved(false), 2000);
      } else {
        router.push('/');
      }
    },
  });

  // First run is a guided wizard (issue #244). Step 1 creates the profile,
  // which flips isCreatePath to false, so the latch keeps the wizard
  // mounted until it finishes itself (it navigates away).
  const [wizardLatched, setWizardLatched] = useState(false);
  if (isCreatePath && !wizardLatched) setWizardLatched(true);

  if (wizardLatched || isCreatePath) {
    return (
      <FirstRunWizard
        handle={handle}
        onHandleChange={(value) => {
          handleEditedByUser.current = true;
          setHandle(value);
        }}
        onRegenerate={() => {
          handleEditedByUser.current = false;
          generateHandle.mutate();
        }}
        regenerating={generateHandle.isPending}
      />
    );
  }

  return (
    <ScreenContainer scrollUnderDock>
      {/* KeyboardAvoidingView, matching create-thread/index.tsx's exact
          pattern -- this screen never had it, even though it now holds
          multiple real text inputs (handle, radius, and #216/#217's
          prompt answer TextAreas). Without it, iOS Safari's keyboard
          opening doesn't get compensated for: the visible viewport
          shrinks but nothing shrinks the layout to match, so the
          browser's own "scroll the focused input into view" behavior
          can land on the wrong part of the page -- founder-reported
          live, 2026-09-16 (screenshot: tapping into a prompt's answer
          field left the screen showing the FOOTER -- Save profile,
          Sign out, tab bar -- with the keyboard's own suggestion bar
          directly over it, no visible input to type into at all).
          Android is left undefined for the same reason create-thread's
          own comment documents: Expo's default windowSoftInputMode
          ("resize") already handles it natively there. */}
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <YStack flex={1} pt="$6">
          {/* pb added -- issue #205's own investigation: this header had
            ZERO bottom padding of its own, relying entirely on the
            ScrollView's contentContainerStyle paddingTop below for
            separation from scrolled content. Live-verified on the
            structurally identical fixed-header-then-ScrollView pattern
            on preview/index.tsx (headless-Chrome render, 390x550
            viewport): with no padding on the header's own trailing
            edge, scrolled content read as directly overlapping the
            header the moment scroll position moved -- matching this
            app's own reported live bug on this exact screen. */}
          <YStack gap="$2" pb="$4">
            <Heading>{ownProfile.isSuccess ? 'Your profile' : 'Create your profile'}</Heading>
            <Paragraph color="$colorSecondary">
              {ownProfile.isSuccess
                ? 'Update how neighbors see you and how far you search.'
                : "A handle and a search radius are all you need to start seeing what's happening nearby."}
            </Paragraph>
          </YStack>

          {/* ScrollView, not a fixed YStack -- ScreenContainer has no
            scroll of its own (screen-container.tsx), and this form's
            content (handle + radius + the location card, which alone
            grows across five states, some with two lines of copy plus a
            button) can run taller than the viewport on a real phone.
            Same failure preview/index.tsx's own ScrollView already
            documents; unlike that screen this one was missed the first
            time, because every gate this project runs to catch it
            (screenshot.mjs, verify_responsive.mjs) renders in a fixed,
            generously tall desktop-Chromium viewport, not a real mobile
            browser's shorter one. Without a scroll container the
            overflow doesn't scroll -- it silently overlaps the "Create
            profile" button below, since neither the header nor this
            content shrinks by default; reported live from a real iPhone
            (Brave, WebKit) on the deployed Netlify build, 2026-09-03. */}
          {/* Plain ScrollView, not NestableScrollContainer -- this
              screen used to hold the photo grid inline (a real
              NestableDraggableFlatList, which genuinely needs the
              Nestable container to coordinate scroll/drag gesture
              arbitration), but that grid was extracted to its own
              screen months ago (issue #80/#238's profile-photos
              split) and this file was never switched back off the
              heavier container it no longer needs -- confirmed via
              grep: zero NestableDraggableFlatList/drag content
              anywhere in this file now. Founder-reported live,
              2026-09-18 (screenshot): on a real mobile browser this
              screen's content below the Basics card was genuinely
              unreachable -- the Location & Radius card's "Share
              location" button was clipped and the fixed "Save
              profile" footer appeared to sit directly on top of it,
              exactly the class of real-device scroll failure this
              screen's OWN prior comment already anticipated
              (headless-Chrome's fixed, generously tall viewport never
              catches it). NestableScrollContainer wraps a heavier
              gesture-arbitration layer designed to negotiate with a
              nested draggable list -- with nothing left to negotiate
              with, it's excess fragility this screen doesn't need
              anymore; a plain ScrollView is the same primitive that
              already works correctly on profile-photos and
              profile-prompts. */}
          <ScrollView
            style={{ flex: 1 }}
            {...dockScroll}
            contentContainerStyle={{ paddingTop: space[6], paddingBottom: space[6] + dockClearance, gap: space[5] }}
            // Default 'never' swallows the first tap on anything inside
            // this ScrollView while the keyboard is up (RN ScrollView.js),
            // just to dismiss it -- this one holds the handle Input AND
            // the LocationCard's buttons, so typing a handle then tapping
            // Share location would otherwise cost two taps. Flagged in
            // PR #56 review.
            keyboardShouldPersistTaps="handled"
          >
            {/* Basics grouped into one labeled card section -- founder,
                2026-09-16: "less cluttered looking profile page". Handle
                and radius used to sit as two ungrouped fields directly
                in the scroll flow, the only part of this screen with no
                visual container at all (Location/Prompts/Photos each
                already have their own card/label). One card + an
                eyebrow label gives this screen the same "distinct,
                scannable sections" structure the rest of it has,
                without a full wizard/multi-step rebuild -- that's a
                separate, later information-architecture decision, not
                this pass's scope. */}
            {ownProfile.isSuccess && (
              <ProfileNudgeCard
                photoCount={ownProfile.data.photos.length}
                promptCount={ownProfile.data.prompts.length}
              />
            )}

            <YStack gap="$2">
              <Text
                color="$colorTertiary"
                fontSize="$meta"
                lineHeight="$meta"
                fontWeight="$semibold"
                textTransform="uppercase"
                letterSpacing={0.4}
              >
                Basics
              </Text>
              <SolidSurface style={{ padding: space[4] }}>
                <YStack gap="$4">
                  <YStack gap="$1">
                    <XStack alignItems="center" justifyContent="space-between">
                      <Label htmlFor="profile-handle" color="$colorSecondary">
                        Handle
                      </Label>
                      {/* Only on the create path -- an existing account's
                          real handle (the edit path) shouldn't offer to
                          throw it away for a random one. */}
                      {isCreatePath && (
                        <TextLink
                          fontSize="$meta"
                          lineHeight="$meta"
                          onPress={() => {
                            if (generateHandle.isPending) return;
                            // A deliberate request for a new value --
                            // clears the "user has typed" guard so this
                            // tap's own result actually lands.
                            handleEditedByUser.current = false;
                            generateHandle.mutate();
                          }}
                        >
                          {generateHandle.isPending ? 'Generating...' : 'Regenerate'}
                        </TextLink>
                      )}
                    </XStack>
                    <Input
                      id="profile-handle"
                      value={handle}
                      onChangeText={(value) => {
                        handleEditedByUser.current = true;
                        setHandle(value);
                      }}
                      placeholder="e.g. quietwanderer"
                      autoCapitalize="none"
                      autoCorrect={false}
                      textContentType="username"
                    />
                    {/* Handle had no helper at all, unlike radius below it --
                      nothing on screen explained why "Create profile" stayed
                      disabled/inert with an empty handle. Reported live: user
                      clicked the button with location already granted and
                      radius filled in, saw no response, no indication the
                      handle was the blocker. Mirrors radius's own always-
                      visible helper pattern instead of a silent disabled
                      state, 2026-09-03. */}
                    {!handleValid && handle.trim() !== '' && (
                      <Paragraph color="$destructive" fontSize="$meta" lineHeight="$meta">
                        Handles can use {HANDLE_POLICY_COPY.toLowerCase()}
                      </Paragraph>
                    )}
                    {!handleValid && (
                      <Text color="$destructive" fontSize="$meta" lineHeight="$meta">
                        Enter a handle to continue.
                      </Text>
                    )}
                  </YStack>
                </YStack>
              </SolidSurface>
            </YStack>

            <YStack gap="$2">
              <Text
                color="$colorTertiary"
                fontSize="$meta"
                lineHeight="$meta"
                fontWeight="$semibold"
                textTransform="uppercase"
                letterSpacing={0.4}
              >
                Location & radius
              </Text>
              {/* Issue #57: the raw "Search radius (meters)" numeric
                  Input (previously in the Basics card above) moved here
                  -- radius has no meaning without a location to center
                  it on, so pairing them in one widget/section is more
                  honest about that dependency than the old two-separate-
                  fields layout ever was. */}
              <LocationRadiusPicker location={location} radiusM={radiusM} onRadiusChange={setRadiusM} />
            </YStack>

            {/* Issue #238's profile-prompts split (bank picker +
                freeform fallback moved to its own screen) and issue
                #80's profile-photos split (the Instagram-style
                drag-and-drop grid, saved independently of this form's
                own PATCH) both still route through their own screens
                unchanged -- the only thing that changed is what this
                entry point looks like. Issue #289's own critique: a
                bare "Manage prompts"/"Manage photos" text link told the
                user nothing about whether they'd actually set any --
                Nielsen #6, Recognition Rather Than Recall, you couldn't
                tell from your own profile without opening the
                sub-screen. Real counts instead: `${count} of
                ${MAX_PROMPTS} set` mirrors profile-prompts/index.tsx's
                own copy register (its Paragraph: "Pick up to
                {MAX_PROMPTS}..."); Photos has no fixed target count the
                same way (MAX_PHOTOS is a ceiling, not a suggested
                number to reach), so it reads as a plain has/hasn't
                instead of manufacturing a fake "of N" target. Grouped
                into one SettingsSection -- two related profile-content
                entries read as one unit, not two separately-labeled
                orphan sections. */}
            {ownProfile.isSuccess && (
              <YStack gap="$2">
                <Text
                  color="$colorTertiary"
                  fontSize="$meta"
                  lineHeight="$meta"
                  fontWeight="$semibold"
                  textTransform="uppercase"
                  letterSpacing={0.4}
                >
                  Profile content
                </Text>
                <SettingsSection>
                  <SettingsRow
                    label="Prompts"
                    value={`${ownProfile.data.prompts.length} of ${MAX_PROMPTS} set`}
                    onPress={() => router.push('/profile-prompts')}
                  />
                  <SettingsRow
                    label="Photos"
                    value={ownProfile.data.photos.length > 0 ? `${ownProfile.data.photos.length} added` : 'None yet'}
                    onPress={() => router.push('/profile-photos')}
                  />
                </SettingsSection>
              </YStack>
            )}

            {/* Decision 10 (issue #565): gender and age range move below
                the content people actually see, under "Optional details".
                The reason line states the backend's real rule
                (visibleDemographic, internal/http/profile.go): any viewer
                of the profile sees these until the owner hides them --
                not matches-only -- and hiding is reciprocal (a viewer who
                hides their own sees nobody's). hideDemographics keeps
                working exactly as before; only placement and copy moved. */}
            <YStack gap="$2">
              <Text
                color="$colorTertiary"
                fontSize="$meta"
                lineHeight="$meta"
                fontWeight="$semibold"
                textTransform="uppercase"
                letterSpacing={0.4}
              >
                Optional details
              </Text>
              <Text color="$colorSecondary" fontSize="$meta" lineHeight="$meta">
                Anyone who opens your profile can see these until you hide them -- and hiding them hides everyone
                else&apos;s from you too.
              </Text>
              <SolidSurface style={{ padding: space[4] }}>
                <YStack gap="$4">
                  <YStack gap="$1">
                    <Label htmlFor="profile-gender" color="$colorSecondary">
                      Gender (optional)
                    </Label>
                    <Input
                      id="profile-gender"
                      value={gender}
                      onChangeText={setGender}
                      placeholder="e.g. woman, man, non-binary"
                      autoCapitalize="none"
                      autoCorrect={false}
                      maxLength={30}
                    />
                  </YStack>

                  <YStack gap="$1">
                    <Label color="$colorSecondary">Age range (optional)</Label>
                    <AgeRangePicker value={ageRange} onValueChange={setAgeRange} />
                  </YStack>

                  {/* Issue #315: default is ON (both fields, when set,
                      are visible pre-reveal) -- this Toggle is the
                      explicit opt-out, not an opt-in. Only shown when
                      there's actually something to hide: a Profile with
                      neither field filled in has nothing this Toggle
                      controls. The section line above already states the
                      reciprocity rule, so the row keeps only the
                      short-form remainder. */}
                  {(gender.trim() !== '' || ageRange !== '') && (
                    <XStack alignItems="center" justifyContent="space-between" gap="$3">
                      <YStack flex={1} gap="$1">
                        <Text color="$color" fontWeight="$semibold">
                          Hide from my profile
                        </Text>
                        <Text color="$colorSecondary" fontSize="$meta" lineHeight="$meta">
                          You won&apos;t see anyone else&apos;s either.
                        </Text>
                      </YStack>
                      <Toggle
                        value={hideDemographics}
                        onValueChange={setHideDemographics}
                        accessibilityLabel="Hide gender and age range from my profile"
                      />
                    </XStack>
                  )}
                </YStack>
              </SolidSurface>
            </YStack>

            {/* Save (and the Settings link below it) moved INLINE, out
                of a fixed footer -- founder, 2026-09-18: a persistent
                bottom bar "wasted space" and "persistently blocked the
                screen" rather than reading as the natural end of the
                form. This is a real, deliberate behavior change (Save
                is now only reachable by scrolling to the bottom, same
                as any other field), not just a style tweak -- matches
                how Photos/Prompts's own "Manage" links already read as
                plain content, not chrome. pt="$6" (no borderTopWidth --
                that divider existed specifically to mark the boundary
                between scrollable content and a FIXED bar; with Save
                now just another scrolled block, a plain gap reads as
                the natural spacing between sections, not a seam). */}
            <YStack gap="$3" pt="$6">
              {update.isError && (
                <Paragraph color="$destructive" textAlign="center">
                  {errorMessage(update.error)}
                </Paragraph>
              )}

              {justSaved && (
                <Paragraph color="$feedbackSuccessText" textAlign="center" fontWeight="$semibold">
                  Saved
                </Paragraph>
              )}

              <PrimaryButton
                disabled={!canSubmit}
                loading={update.isPending}
                onPress={() => {
                  if (update.isPending || !canSubmit) return;
                  if (location.phase.phase !== 'granted') return; // implied by canSubmit; narrows the phase type
                  update.mutate({
                    handle: handle.trim(),
                    lat: location.phase.coords.lat,
                    lng: location.phase.coords.lng,
                    radiusM,
                    gender: gender.trim(),
                    ageRange,
                    hideDemographics,
                  });
                }}
              >
                {update.isPending ? (
                  <ActivityIndicator color="white" />
                ) : ownProfile.isSuccess ? (
                  'Save profile'
                ) : (
                  'Create profile'
                )}
              </PrimaryButton>

              {/* Issue #292: this screen used to terminate on a disabled
                  grey button with no explanation of why -- most commonly
                  hit by a RETURNING user (ownProfile.isSuccess already
                  true, a real saved profile exists) whose location.phase
                  resets to idle on every fresh page load (useCurrentLocation
                  never auto-requests geolocation -- see LocationCard's own
                  "Share location" idle state), so canSubmit reads false
                  even though nothing is actually wrong. Not "no changes
                  to save" (the issue's other suggested option) -- that
                  would be factually wrong here, since the real reason is
                  a missing required field, not an absence of edits. */}
              {!canSubmit && !update.isPending && (
                <Text color="$colorTertiary" fontSize="$meta" lineHeight="$meta" textAlign="center">
                  {!handleValid
                    ? 'Enter a handle above to save.'
                    : ownProfile.isSuccess
                      ? 'Share your location above to save changes.'
                      : 'Share your location above to create your profile.'}
                </Text>
              )}

              {/* Issue #80: verification status, Reveals & connections,
                  Safety Center, and Sign out all moved to their own
                  Settings screen (reachable via this one link) -- this
                  form is for editing what OTHER people see, not account
                  housekeeping, and the prior version of this footer (a
                  status line + a links row + Sign out, all stacked after
                  Save) crowded this screen with content unrelated to
                  "edit your profile" (founder feedback, 2026-09-18).
                  Founder, 2026-09-19: "accessing settings from profile"
                  -- same critique as Settings' own Sign out row, this
                  was a bare centered TextLink, the exact idiom #289's
                  SettingsRow was built to replace everywhere else. Same
                  component here for the same reason -- a real row with
                  a chevron and the app's established $card material,
                  not a throwaway link. */}
              <SettingsSection>
                <SettingsRow label="Settings" onPress={() => router.push('/settings')} />
              </SettingsSection>
              <PageEnd>Only what you add here is ever shown to neighbors.</PageEnd>
            </YStack>
          </ScrollView>
        </YStack>
      </KeyboardAvoidingView>
    </ScreenContainer>
  );
}
